import React from 'react';

function scrollTo(id) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

const StepBadge = ({ status }) => {
  const map = {
    done: 'bg-green-500/20 text-green-700 border-green-500/30',
    current: 'bg-primary/20 text-primary-foreground/80 border-primary/30',
    pending: 'bg-muted/40 text-textSecondary border-borderDefault',
    error: 'bg-red-500/20 text-red-700 border-red-500/30',
  };
  return <span className={`text-[10px] px-1.5 py-0.5 rounded border ${map[status] || map.pending}`}>{status}</span>;
};

export default function BuilderSteps({ nodes = [], comparisons = [], sections = {}, validationSummary, dirty }) {
  const nodesCount = nodes?.filter(n => n.type !== 'comparison')?.length || 0;
  const comparisonsCount = (comparisons && comparisons.length) ? comparisons.length : nodes.filter(n=>n.type==='comparison').length;
  const hasAnySection = React.useMemo(() => {
    const values = Object.values(sections || {});
    return values.some((sec) => {
      const root = sec?.root ? sec.root : sec;
      return Array.isArray(root?.children) && root.children.length > 0;
    });
  }, [sections]);

  const isValid = !!validationSummary?.isValid;

  // Derive step statuses
  const steps = [
    {
      key: 'nodes',
      label: '1. Add inputs (nodes)',
      status: nodesCount > 0 ? 'done' : 'current',
      target: 'node-palette',
      hint: nodesCount > 0 ? `${nodesCount} node${nodesCount > 1 ? 's' : ''}` : 'Add at least one indicator/expression',
    },
    {
      key: 'comparisons',
      label: '2. Define comparisons',
      status: nodesCount === 0 ? 'pending' : (comparisonsCount > 0 ? 'done' : 'current'),
      target: 'comparisons',
      hint: comparisonsCount > 0 ? `${comparisonsCount} comparison${comparisonsCount > 1 ? 's' : ''}` : 'Compare series or constants',
    },
    {
      key: 'sections',
      label: '3. Build sections logic',
      status: (nodesCount === 0 || comparisonsCount === 0) ? 'pending' : (hasAnySection ? 'done' : 'current'),
      target: 'sections',
      hint: hasAnySection ? 'Conditions assigned' : 'Assign comparisons to ENTRY/EXIT',
    },
    {
      key: 'validation',
      label: '4. Fix errors',
      status: isValid ? 'done' : 'error',
      target: 'validation',
      hint: isValid ? 'All checks passed' : 'Open validation panel',
    },
    {
      key: 'save',
      label: '5. Save draft',
      status: isValid ? (dirty ? 'current' : 'done') : 'pending',
      target: 'save-area',
      hint: isValid ? (dirty ? 'Ready to save' : 'Saved') : 'Resolve errors first',
    },
  ];

  return (
    <div className="border rounded-md p-2.5 bg-card/70">
      <div className="flex flex-wrap gap-2 items-center">
        {steps.map((s) => (
          <button
            key={s.key}
            type="button"
            onClick={() => scrollTo(s.target)}
            className={`flex items-center gap-2 px-2.5 py-1 rounded border text-[11px] transition-colors ${
              s.status === 'done'
                ? 'bg-green-500/10 border-green-500/30 hover:bg-green-500/20'
                : s.status === 'error'
                ? 'bg-red-500/10 border-red-500/30 hover:bg-red-500/20'
                : s.status === 'current'
                ? 'bg-primary/10 border-primary/30 hover:bg-primary/20'
                : 'bg-muted/40 border-borderDefault hover:bg-muted/60'
            }`}
            title={s.hint}
          >
            <span>{s.label}</span>
            <StepBadge status={s.status} />
          </button>
        ))}
      </div>
    </div>
  );
}
